import { useOrgUnit, useDeleteOrgUnit } from '../../../hooks/useOrgUnits';
import type { OrgUnit } from '../../../types/orgUnit';

interface Props {
  orgUnitId: string;
  orgUnits: OrgUnit[];
  onClose: () => void;
  onDeleted: () => void;
}

export function OrgUnitCascadeDeleteDialog({ orgUnitId, orgUnits, onClose, onDeleted }: Props) {
  const detailQuery = useOrgUnit(orgUnitId);
  const deleteMut = useDeleteOrgUnit();

  function subtree(rootId: string): OrgUnit[] {
    const result: OrgUnit[] = [];
    const queue = [rootId];
    while (queue.length > 0) {
      const id = queue.shift()!;
      const unit = orgUnits.find((u) => u.org_unit_id === id);
      if (!unit || unit.deleted_at) continue;
      result.push(unit);
      for (const child of orgUnits) {
        if (child.parent_id === id && !child.deleted_at) {
          queue.push(child.org_unit_id);
        }
      }
    }
    return result;
  }

  const tree = subtree(orgUnitId);
  const detail = detailQuery.data;

  async function handleConfirm() {
    try {
      await deleteMut.mutateAsync({ id: orgUnitId, cascade: true });
      onDeleted();
    } catch (err) {
      console.error('Cascade delete failed:', err);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-ems-bg-card border border-ems-border rounded-lg w-full max-w-md p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="font-semibold text-ems-red text-sm">
            Delete "{detail?.name ?? '…'}" and everything below?
          </div>
        </div>

        <div className="bg-ems-forest/40 rounded p-3 text-xs text-ems-text-primary font-mono mb-3">
          <div className="text-ems-green mb-1">Subtree — will be soft-deleted</div>
          {tree.map((u) => (
            <div
              key={u.org_unit_id}
              style={{ paddingLeft: `${u.parent_id ? 20 : 8}px` }}
            >
              • {u.name}{' '}
              <span className="text-ems-text-muted">{u.type}</span>
            </div>
          ))}
        </div>

        {detail && detail.site_count > 0 && (
          <div className="bg-ems-forest/40 rounded p-3 text-xs text-ems-amber mb-3">
            Sites — {detail.site_count} site{detail.site_count === 1 ? '' : 's'} will be unbound
          </div>
        )}

        {detail && detail.member_count > 0 && (
          <div className="bg-ems-red/15 rounded p-3 text-xs text-ems-red mb-3">
            ⚠ {detail.member_count} user{detail.member_count === 1 ? '' : 's'} will lose
            group-derived access
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded text-xs border border-ems-border text-ems-text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={deleteMut.isPending}
            className="px-3 py-1.5 rounded text-xs bg-ems-red text-ems-bg-card disabled:opacity-40"
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete cascade'}
          </button>
        </div>
      </div>
    </div>
  );
}
